import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  MapPin,
  Phone,
  MessageCircle,
  Clock,
  Send,
  Heart,
  Facebook
} from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import './Contact.css';

const Contact = () => {
  const [formState, setFormState] = useState({
    name: '',
    phone: '',
    clinic: 'المقطم',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const whatsappText = `مرحباً د. محمد خالد. أنا ${formState.name}، أود الاستفسار بخصوص فرع ${formState.clinic}. رسالتي: ${formState.message}`;
    const whatsappUrl = `https://wa.me/201126276505?text=${encodeURIComponent(whatsappText)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const clinicsInfo = [
    {
      name: 'عيادة المقطم',
      address: 'عمارة 163 بجوار صيدلية ناريمان',
      times: 'الأحد والثلاثاء: 8 - 11 م | الخميس: 1 - 4 ظ',
      phone: '01126276505',
      landline: '0226688042',
      type: 'whatsapp'
    },
    {
      name: 'عيادة الشروق',
      address: 'بانوراما مول - الدور الأول',
      times: 'مواعيد متعددة طوال الأسبوع',
      phone: '01104044537',
      type: 'whatsapp'
    },
    {
      name: 'عيادة مدينة نصر',
      address: 'مركز كابيتال للإنجاب - 49 شارع محيي الدين عبد الحميد من عباس العقاد',
      times: 'الاثنين والخميس: 10 - 12 ص',
      phone: '01140147994',
      type: 'whatsapp'
    },
    {
      name: 'مستشفى الزراعيين (الدقي)',
      address: 'الدقي - مستشفى الزراعيين (عيادة النساء)',
      times: 'السبت: 12 - 2 م | الأربعاء: 10 - 12 م',
      phone: '02333776677',
      extension: 'داخلي 161',
      type: 'call'
    }
  ];

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-bg">
          <div className="contact-hero-gradient"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="contact-hero-content"
        >
          <div className="contact-hero-badge">
            <Heart size={14} />
            <span>تواصل معنا المباشر</span>
          </div>
          <h1>يسعدنا دائماً تواصلكِ معنا</h1>
          <p>اختاري الفرع المناسب أو تواصلي معنا عبر قنواتنا المباشرة للحصول على الرعاية المناسبة</p>
        </motion.div>
      </section>

      <div className="contact-container">
        {/* Left: Contact Form & Socials */}
        <motion.div
          className="contact-sidebar"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Emergency Card */}
          <div className="contact-emergency-card">
            <div className="emergency-glow"></div>
            <div className="emergency-header">
              <span className="emergency-icon-pulse">🚨</span>
              <h3>طبيب الطوارئ (24/7)</h3>
            </div>
            <p>في الحالات الطارئة والولادات، يرجى الاتصال فوراً بالخط الساخن للطوارئ:</p>
            <a href="tel:+201289444600" className="emergency-call-btn">
              <Phone size={18} />
              <span>01289444600</span>
            </a>
          </div>

          {/* Social Links Card */}
          <div className="contact-social-card">
            <h3>تابعونا على منصاتنا</h3>
            <p>شاهدي النصائح الطبية وآخر الأخبار الطبية للدكتور محمد خالد:</p>
            <div className="social-buttons">
              <a href="https://www.facebook.com/share/1DGkN6yJi7/" target="_blank" rel="noopener noreferrer" className="social-btn facebook">
                <Facebook size={20} />
                <span>صفحة الفيسبوك</span>
              </a>
              <a href="https://www.tiktok.com/@dr.mohamed.khaled.t" target="_blank" rel="noopener noreferrer" className="social-btn tiktok">
                <FaTiktok size={20} />
                <span>حساب التيك توك</span>
              </a>
            </div>
          </div>

          {/* Quick Message Form */}
          <div className="quick-form-card">
            <h3>استفسار سريع</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>الاسم الكامل</label>
                <input
                  type="text"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                  placeholder="أدخلي اسمكِ هنا"
                  required
                />
              </div>
              <div className="form-group">
                <label>رقم الهاتف</label>
                <input
                  type="tel"
                  name="phone"
                  value={formState.phone}
                  onChange={handleChange}
                  placeholder="مثال: 01012345678"
                  required
                />
              </div>
              <div className="form-group">
                <label>الفرع المعني</label>
                <select name="clinic" value={formState.clinic} onChange={handleChange}>
                  <option value="المقطم">المقطم</option>
                  <option value="الشروق">الشروق</option>
                  <option value="مدينة نصر">مدينة نصر</option>
                  <option value="الدقي">الدقي</option>
                </select>
              </div>
              <div className="form-group">
                <label>رسالتكِ أو استفساركِ</label>
                <textarea
                  name="message"
                  value={formState.message}
                  onChange={handleChange}
                  placeholder="اكتبي استفساركِ هنا..."
                  rows="4"
                  required
                ></textarea>
              </div>
              <button type="submit" className="form-submit-btn">
                <span>إرسال عبر واتساب</span>
                <Send size={16} />
              </button>
            </form>
          </div>
        </motion.div>

        {/* Right: Clinics Cards */}
        <motion.div
          className="clinics-contact-grid"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title-contact">فروعنا وعياداتنا</h2>
          {clinicsInfo.map((clinic) => (
            <motion.div
              key={clinic.name}
              className="clinic-contact-card"
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <div className="card-decor"></div>
              <div className="clinic-card-header">
                <div className="map-icon-wrapper">
                  <MapPin size={24} />
                </div>
                <h3>{clinic.name}</h3>
              </div>

              <div className="clinic-card-body">
                <div className="info-detail-item">
                  <MapPin size={16} className="detail-icon" />
                  <span>{clinic.address}</span>
                </div>
                <div className="info-detail-item">
                  <Clock size={16} className="detail-icon" />
                  <span>{clinic.times}</span>
                </div>
                {clinic.landline && (
                  <div className="info-detail-item">
                    <Phone size={16} className="detail-icon" />
                    <span>التليفون الأرضي: {clinic.landline}</span>
                  </div>
                )}
              </div>

              <div className="clinic-card-footer">
                {clinic.type === 'whatsapp' ? (
                  <a
                    href={`https://wa.me/20${clinic.phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="clinic-action-btn whatsapp-btn"
                  >
                    <MessageCircle size={18} />
                    <span>تواصل واتساب ({clinic.phone})</span>
                  </a>
                ) : (
                  <a href={`tel:${clinic.phone}`} className="clinic-action-btn phone-btn">
                    <Phone size={18} />
                    <span>اتصال بالعيادة ({clinic.phone} {clinic.extension})</span>
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
